import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import tokenManager from '../utils/tokenManager';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

const SystemResourceGraphs = ({ apiBaseUrl }) => {
  const [cpuData, setCpuData] = useState([]);
  const [memoryData, setMemoryData] = useState([]);
  const [diskData, setDiskData] = useState([]);
  const [currentMetrics, setCurrentMetrics] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const MAX_DATA_POINTS = 60; // 5 minutes at 5-second intervals

  const getThresholdColor = (value, type) => {
    let greenThreshold, yellowThreshold;
    
    switch (type) {
      case 'cpu':
        greenThreshold = 60;
        yellowThreshold = 80;
        break;
      case 'memory':
      case 'disk':
        greenThreshold = 70;
        yellowThreshold = 85;
        break;
      default:
        return '#10b981'; // green
    }

    if (value < greenThreshold) return '#10b981'; // green
    if (value < yellowThreshold) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const getStatusBadge = (value, type) => {
    const color = getThresholdColor(value, type);
    let bgColor, textColor, borderColor;

    if (color === '#10b981') {
      bgColor = 'bg-green-100';
      textColor = 'text-green-800';
      borderColor = 'border-green-300';
    } else if (color === '#f59e0b') {
      bgColor = 'bg-yellow-100';
      textColor = 'text-yellow-800';
      borderColor = 'border-yellow-300';
    } else {
      bgColor = 'bg-red-100';
      textColor = 'text-red-800';
      borderColor = 'border-red-300';
    }

    return { bgColor, textColor, borderColor };
  };

  const fetchSystemMetrics = useCallback(async () => {
    try {
      const response = await api.get('/dev/system-metrics');
      
      const data = response.data;
      const timestamp = new Date(data.timestamp).toLocaleTimeString();

      setCurrentMetrics(data);
      setError(null);

      // Update CPU data
      setCpuData(prev => {
        const newData = [...prev, {
          time: timestamp,
          usage: parseFloat(data.cpu.usage_percent.toFixed(1))
        }];
        return newData.slice(-MAX_DATA_POINTS);
      });

      // Update Memory data
      setMemoryData(prev => {
        const newData = [...prev, {
          time: timestamp,
          usage: parseFloat(data.memory.usage_percent.toFixed(1)),
          used: data.memory.used_mb,
          free: data.memory.free_mb
        }];
        return newData.slice(-MAX_DATA_POINTS);
      });

      // Update Disk data
      setDiskData(prev => {
        const newData = [...prev, {
          time: timestamp,
          usage: parseFloat(data.disk.usage_percent.toFixed(1)),
          used: data.disk.used_gb,
          free: data.disk.free_gb
        }];
        return newData.slice(-MAX_DATA_POINTS);
      });

      setIsLoading(false);
    } catch (err) {
      if (err.response && err.response.status === 404) {
        setError('Dev monitoring is disabled');
      } else {
        setError(err.message);
      }
      setIsLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    fetchSystemMetrics();
  }, [fetchSystemMetrics]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchSystemMetrics, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchSystemMetrics]);

  if (error) {
    return (
      <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <p className="text-yellow-800">Metrics unavailable: {error}</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mt-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <p className="text-gray-600">Loading system metrics...</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">
          System Resource Usage (DEV)
        </h2>
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            className="sr-only peer"
          />
          <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-700">Auto-refresh</span>
        </label>
      </div>

      {/* Current Metrics Summary */}
      {currentMetrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* CPU Summary */}
          <div className={`p-4 rounded-lg border ${getStatusBadge(currentMetrics.cpu.usage_percent, 'cpu').bgColor} ${getStatusBadge(currentMetrics.cpu.usage_percent, 'cpu').borderColor}`}>
            <div className="flex justify-between items-center">
              <span className={`font-semibold ${getStatusBadge(currentMetrics.cpu.usage_percent, 'cpu').textColor}`}>
                CPU Usage
              </span>
              <span className={`text-2xl font-bold ${getStatusBadge(currentMetrics.cpu.usage_percent, 'cpu').textColor}`}>
                {currentMetrics.cpu.usage_percent.toFixed(1)}%
              </span>
            </div>
          </div>

          {/* Memory Summary */}
          <div className={`p-4 rounded-lg border ${getStatusBadge(currentMetrics.memory.usage_percent, 'memory').bgColor} ${getStatusBadge(currentMetrics.memory.usage_percent, 'memory').borderColor}`}>
            <div className="flex justify-between items-center mb-2">
              <span className={`font-semibold ${getStatusBadge(currentMetrics.memory.usage_percent, 'memory').textColor}`}>
                RAM Usage
              </span>
              <span className={`text-2xl font-bold ${getStatusBadge(currentMetrics.memory.usage_percent, 'memory').textColor}`}>
                {currentMetrics.memory.usage_percent.toFixed(1)}%
              </span>
            </div>
            <div className={`text-sm ${getStatusBadge(currentMetrics.memory.usage_percent, 'memory').textColor}`}>
              {currentMetrics.memory.used_mb} MB / {currentMetrics.memory.total_mb} MB
            </div>
          </div>

          {/* Disk Summary */}
          <div className={`p-4 rounded-lg border ${getStatusBadge(currentMetrics.disk.usage_percent, 'disk').bgColor} ${getStatusBadge(currentMetrics.disk.usage_percent, 'disk').borderColor}`}>
            <div className="flex justify-between items-center mb-2">
              <span className={`font-semibold ${getStatusBadge(currentMetrics.disk.usage_percent, 'disk').textColor}`}>
                Disk Usage
              </span>
              <span className={`text-2xl font-bold ${getStatusBadge(currentMetrics.disk.usage_percent, 'disk').textColor}`}>
                {currentMetrics.disk.usage_percent.toFixed(1)}%
              </span>
            </div>
            <div className={`text-sm ${getStatusBadge(currentMetrics.disk.usage_percent, 'disk').textColor}`}>
              {currentMetrics.disk.used_gb} GB / {currentMetrics.disk.total_gb} GB
            </div>
          </div>
        </div>
      )}

      {/* Graphs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* CPU Graph */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">CPU Usage Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={cpuData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                label={{ value: '%', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="usage" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={false}
                name="CPU %"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Memory Graph */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Memory Usage Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={memoryData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                label={{ value: '%', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip />
              <Legend />
              <Area 
                type="monotone" 
                dataKey="usage" 
                stroke="#8b5cf6" 
                fill="#8b5cf6"
                fillOpacity={0.6}
                name="Memory %"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Disk Graph */}
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">Disk Usage Over Time</h3>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={diskData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 10 }}
                interval="preserveStartEnd"
              />
              <YAxis 
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                label={{ value: '%', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="usage" 
                stroke="#10b981" 
                strokeWidth={2}
                dot={false}
                name="Disk %"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Legend for color coding */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h4 className="text-sm font-semibold mb-2 text-gray-700">Threshold Legend:</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span className="text-gray-600">CPU: &lt;60% | RAM/Disk: &lt;70%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span className="text-gray-600">CPU: 60-80% | RAM/Disk: 70-85%</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span className="text-gray-600">CPU: &gt;80% | RAM/Disk: &gt;85%</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemResourceGraphs;
